import React from 'react';
import { Box, CircularProgress, Alert, Typography, Paper } from '@mui/material';
import { Lock as LockIcon, ErrorOutline } from '@mui/icons-material';

/**
 * Reusable component for dashboard sections with loading, error, and permission states
 *
 * @param {Object} props
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error type ('NO_PERMISSION', 'API_ERROR', or custom message)
 * @param {string} props.errorMessage - Custom error message
 * @param {any} props.data - Section data (can be array, object, or any value)
 * @param {boolean} props.isEmpty - Optional flag to indicate empty data state
 * @param {string} props.emptyMessage - Message to show when data is empty
 * @param {ReactNode} props.children - Content to render when data is loaded successfully
 * @param {string} props.minHeight - Minimum height for the section (default: '300px')
 */
const DashboardSection = ({
  loading,
  error,
  errorMessage,
  data,
  isEmpty,
  emptyMessage = 'No data available',
  children,
  minHeight = '300px'
}) => {
  // Loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={minHeight}
      >
        <CircularProgress />
      </Box>
    );
  }

  // No permission error
  if (error === 'NO_PERMISSION') {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight={minHeight}
        sx={{ backgroundColor: '#f5f5f5', borderRadius: 1, p: 3 }}
      >
        <LockIcon sx={{ fontSize: 48, color: '#9e9e9e', mb: 2 }} />
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Permission Required
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          {errorMessage || 'You do not have permission to view this section'}
        </Typography>
      </Box>
    );
  }

  // API or other errors
  if (error === 'API_ERROR' || (error && error !== 'NO_PERMISSION')) {
    return (
      <Box minHeight={minHeight} display="flex" alignItems="center">
        <Alert
          severity="error"
          icon={<ErrorOutline />}
          sx={{ width: '100%' }}
        >
          {errorMessage || 'Failed to load data. Please try refreshing the page.'}
        </Alert>
      </Box>
    );
  }

  // Empty data state
  if (isEmpty || data === null || data === undefined || (Array.isArray(data) && data.length === 0)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={minHeight}
        sx={{ backgroundColor: '#fafafa', borderRadius: 1, p: 3 }}
      >
        <Typography variant="body1" color="textSecondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  // Success state - render children
  return <>{children}</>;
};

export default DashboardSection;
